import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '../../services/api';
import { normalizeSchema } from '../../services/apiDomains/schemas';
import { Button, Badge, Input, Select, SchemaForm, useToast } from '../../components/common';
import SchemaBuilder from '../../components/SchemaBuilder';
import SchemaNetworkGraph from '../../components/SchemaNetworkGraph';
import { useAuth } from '../../contexts/AuthContext';
import { useGroup } from '../../contexts/GroupContext';
import { Search, Plus, ChevronDown, GitFork, Layers, Link, ShieldCheck } from 'lucide-react';
import { ChevronRightIcon, Plate, HubSpread, FillGrid, PaneBar } from '@ofself/shell';
import { schemaLabel } from '../../utils/schemaUtils';
import { dateOf, plural } from '../../nucleus/format';
import './DevSchemasTab.css';
// Reuse modal + transfer styling from MyAppsTab (CSS is global in CRA)
import './MyAppsTab.css';

function DevSchemasTab() {
  const toast = useToast();
  const { user } = useAuth();
  const { groups, activeGroupId } = useGroup();

  // View state: 'landing' | 'discover' | 'builder'
  const [view, setView] = useState('landing');
  // The library folds open BELOW the hub rather than replacing it. `view` stays
  // for the deep-link case (navigateToSchemaById opens the library on a schema).
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Discover panel mode: 'list' | 'graph'
  const [listMode, setListMode] = useState('list');

  // Data
  const [schemas, setSchemas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Discover filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  // The library's reading + the filter flyout (the PaneBar chrome).
  const [libView, setLibView] = useState('blocks');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState(''); // '' | 'system' | 'mine'

  // Detail
  const [selectedSchemaId, setSelectedSchemaId] = useState(null);
  const [detailSchema, setDetailSchema] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [schemaViewMode, setSchemaViewMode] = useState('form');

  // Builder state
  const [builderMode, setBuilderMode] = useState('create'); // 'create' | 'extend' | 'update'
  const [builderParentSchema, setBuilderParentSchema] = useState(null);
  const [builderExistingSchema, setBuilderExistingSchema] = useState(null);
  const [previousView, setPreviousView] = useState('landing');

  // Expanded parent schema fields in detail panel
  const [parentExpanded, setParentExpanded] = useState(false);
  const [parentSchemaData, setParentSchemaData] = useState(null);

  // Test
  const [testJson, setTestJson] = useState('{}');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // All schemas (flat, for extends dropdown etc.)
  const [allSchemas, setAllSchemas] = useState([]);

  // Transfer ownership
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferType, setTransferType] = useState('user'); // 'user' | 'group' | 'personal'
  const [transferTarget, setTransferTarget] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const userSearchTimer = useRef(null);

  const loadSchemas = useCallback(async () => {
    setLoading(true);
    try {
      const [schemasData, catsData] = await Promise.all([
        api.request('/schemas?include_json_schema=true&include_apps=true&include_extended_by=true&group_by_uri=true&limit=200').then((d) => ({ ...d, schemas: (d?.schemas || []).map(normalizeSchema) })),
        api.request('/schemas/categories'),
      ]);
      setSchemas(schemasData.schemas || []);
      setCategories(catsData.categories || []);
      setLoadError(null);
    } catch (err) {
      // A SILENT CATCH RENDERS AS "0 SCHEMAS". An expired token, a dropped
      // connection and a 500 all used to look identical to an empty account —
      // which reads as the UI having lost the data rather than the request
      // having failed. Keep the reason and say it.
      console.error('[nucleus] /schemas fetch failed:', err);
      setLoadError(err?.message || 'Could not reach the schema service.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAllSchemas = useCallback(async () => {
    try {
      const data = await api.request('/schemas?include_json_schema=true&limit=200');
      if (data?.schemas) data.schemas = data.schemas.map(normalizeSchema);
      setAllSchemas(data.schemas || []);
    } catch (err) {
      console.error('[nucleus] /schemas (all) fetch failed:', err);
    }
  }, []);

  useEffect(() => { loadSchemas(); loadAllSchemas(); }, [loadSchemas, loadAllSchemas]);

  // When selectedSchemaId changes (clicking a card), set detailSchema from list and load versions
  useEffect(() => {
    setTestJson('{}');
    setTestResult(null);
    setParentExpanded(false);
    setParentSchemaData(null);
    if (!selectedSchemaId) { setDetailSchema(null); setVersions([]); return; }
    const schema = schemas.find(s => s.id === selectedSchemaId);
    if (schema) setDetailSchema(schema);
  }, [selectedSchemaId, schemas]);

  useEffect(() => {
    if (!detailSchema?.schema_name) { setVersions([]); return; }
    setLoadingVersions(true);
    api.getSchemaVersions(detailSchema.schema_name)
      .then(setVersions)
      .catch(() => setVersions([]))
      .finally(() => setLoadingVersions(false));
  // Only reload versions when the schema_name changes, not on every detailSchema update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailSchema?.schema_name]);

  // Switch to a different version from the dropdown
  const handleVersionSwitch = async (versionId) => {
    if (versionId === detailSchema?.id) return;
    try {
      const data = normalizeSchema(await api.request(`/schemas/${versionId}?include_json_schema=true`));
      setDetailSchema(data);
    } catch {
      toast.error('Failed to load schema version');
    }
  };

  // Filtered schemas for discover view
  const filteredSchemas = useMemo(() => {
    const s = search.toLowerCase().trim();
    return schemas.filter(schema => {
      // Text search: URI, description, and field names
      if (s) {
        const uriMatch = (schema.schema_name || '').toLowerCase().includes(s)
          || schemaLabel(schema.schema_name || '').toLowerCase().includes(s);
        const descMatch = (schema.description || '').toLowerCase().includes(s);
        // Search in property field names
        const fieldNames = schema.json_schema?.properties
          ? Object.keys(schema.json_schema.properties)
          : [];
        const fieldMatch = fieldNames.some(f => f.toLowerCase().includes(s));
        if (!uriMatch && !descMatch && !fieldMatch) return false;
      }
      if (categoryFilter && schema.category !== categoryFilter) return false;
      if (ownerFilter === 'system' && !schema.is_system) return false;
      if (ownerFilter === 'mine') {
        const isMine = activeGroupId
          ? schema.group_id === activeGroupId
          : schema.owner_id === user?.id && !schema.group_id;
        if (!isMine) return false;
      }
      return true;
    });
  }, [schemas, search, categoryFilter, ownerFilter, user, activeGroupId]);

  const selectedSchema = detailSchema;

  // "Your Schemas" follows the active context: group-owned schemas when browsing
  // as a group, personally-owned only when in personal context.
  const mySchemas = useMemo(() =>
    schemas.filter(s => activeGroupId
      ? s.group_id === activeGroupId
      : s.owner_id === user?.id && !s.group_id
    ),
    [schemas, user, activeGroupId]
  );

  // "Schemas you use" — schemas applied to your nodes (by node count), consolidated
  // from the old /graph?tab=schemas page. Distinct from "Your Schemas" (ones you made).
  const [schemaNodeCounts, setSchemaNodeCounts] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const nodesData = activeGroupId
          ? await api.getGroupNodes(activeGroupId, { limit: 200 })
          : await api.request('/nodes?limit=200');
        const counts = {};
        (nodesData.nodes || []).forEach(n => { if (n.schema_id) counts[n.schema_id] = (counts[n.schema_id] || 0) + 1; });
        setSchemaNodeCounts(counts);
      } catch { /* ignore */ }
    })();
  }, [activeGroupId]);
  const usedSchemas = useMemo(
    () => schemas.filter(s => schemaNodeCounts[s.id] > 0),
    [schemas, schemaNodeCounts]
  );

  // True if the current user can delete/transfer this schema (admin-level):
  // personal owner, OR an active admin of the group that owns it.
  const canManageSchema = useCallback((s) => {
    if (!s || s.is_system) return false;
    if (s.group_id) return groups.find(g => g.id === s.group_id)?.my_role === 'admin';
    return s.owner_id === user?.id;
  }, [groups, user]);

  // True if the current user can create a new version of this schema:
  // personal owner, OR any active member of the group that owns it.
  // Looser than canManageSchema — versioning is collaborative.
  const canVersionSchema = useCallback((s) => {
    if (!s || s.is_system) return false;
    if (s.group_id) return !!groups.find(g => g.id === s.group_id); // any member
    return s.owner_id === user?.id;
  }, [groups, user]);

  // Open builder in different modes
  const openBuilderCreate = () => {
    setBuilderMode('create');
    setBuilderParentSchema(null);
    setBuilderExistingSchema(null);
    setPreviousView(view);
    setView('builder');
  };

  const handleBuilderCancel = () => {
    setView(previousView);
  };

  const handleBuilderSave = async (payload) => {
    try {
      if (builderMode === 'update' && builderExistingSchema) {
        const result = await api.request(`/schemas/${builderExistingSchema.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        toast.success(`New version v${result.version} created`);
        await loadSchemas();
        await loadAllSchemas();
        setSelectedSchemaId(result.id);
        // The library is a band of this page now, not a screen to switch to:
        // unfold it on the schema that was just versioned.
        setLibraryOpen(true);
      } else {
        // In group context, attribute the new schema to the active group.
        const body = activeGroupId && !payload.group_id
          ? { ...payload, group_id: activeGroupId }
          : payload;
        await api.request('/schemas', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        toast.success('Schema created!');
        await loadSchemas();
        await loadAllSchemas();
        setView(previousView === 'builder' ? 'landing' : previousView);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save schema');
    }
  };

  const handleUserSearchChange = (q) => {
    setUserSearchQuery(q);
    setSelectedUser(null);
    setTransferTarget('');
    clearTimeout(userSearchTimer.current);
    if (!q.trim()) { setUserSearchResults([]); return; }
    userSearchTimer.current = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const results = await api.searchUsers(q.trim(), 10);
        setUserSearchResults(results);
      } catch {
        setUserSearchResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
  };

  const selectTransferUser = (u) => {
    setSelectedUser(u);
    setTransferTarget(u.user_id);
    setUserSearchQuery(u.username || '');
    setUserSearchResults([]);
  };

  const openTransferModal = () => {
    if (!selectedSchema) return;
    setTransferType(selectedSchema.group_id ? 'personal' : 'user');
    setTransferTarget('');
    setTransferError('');
    setUserSearchQuery('');
    setUserSearchResults([]);
    setSelectedUser(null);
    setShowTransferModal(true);
  };

  const handleTransfer = async () => {
    if (!selectedSchema) return;
    setTransferLoading(true);
    setTransferError('');
    try {
      const payload =
        transferType === 'user' ? { to_user_id: transferTarget } :
        transferType === 'group' ? { to_group_id: transferTarget } :
        { to_personal: true };
      const result = await api.request(`/schemas/${selectedSchema.id}/transfer`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      toast.success(`Ownership transferred (${plural(result.versions_transferred, 'version')})`);
      setShowTransferModal(false);
      await loadSchemas();
      await loadAllSchemas();
      setSelectedSchemaId(null);
    } catch (err) {
      setTransferError(err.message || 'Transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleTest = async () => {
    let metadata;
    try { metadata = JSON.parse(testJson); }
    catch { setTestResult({ valid: false, errors: ['Invalid JSON — could not parse input'] }); return; }
    setTestLoading(true);
    setTestResult(null);
    try {
      const result = await api.validateSchema(detailSchema.id, metadata);
      setTestResult(result);
    } catch (err) {
      setTestResult({ valid: false, errors: [err.message] });
    } finally {
      setTestLoading(false);
    }
  };

  const navigateToSchema = (schemaName) => {
    const target = schemas.find(s => s.schema_name === schemaName);
    if (target) {
      setSelectedSchemaId(target.id);
      setLibraryOpen(true);
    }
  };

  const navigateToSchemaById = (id) => {
    setSelectedSchemaId(id);
    setLibraryOpen(true);
  };

  // Get field names from json_schema
  const getFieldNames = (schema) => {
    if (!schema?.json_schema?.properties) return [];
    return Object.keys(schema.json_schema.properties);
  };

  // ==============================
  // LANDING VIEW
  // ==============================
  // ── THE HUB IS ONE PAGE (owner call) ──────────────────────────────────
  // My Schemas and the Schema Library were two screens with a button between
  // them. They are siblings now: the schemas you actually use, and beneath them
  // — on its own row in the same column — the door to everything else, which
  // opens IN PLACE rather than replacing what you were looking at.
  // The leaf's second fact: how much of the graph these schemas actually hold.
  const usedSchemaNodeTotal = useMemo(
    () => usedSchemas.reduce((n, s) => n + (schemaNodeCounts[s.id] || 0), 0),
    [usedSchemas, schemaNodeCounts],
  );

  const _leaves = [
    { id: 'mine', name: 'My Schemas', meta: [
      plural(usedSchemas.length, 'schema'),
      plural(usedSchemaNodeTotal, 'node'),
    ] },
    { id: 'library', name: 'Schema Library', meta: [
      plural(schemas.length, 'schema'),
    ] },
  ];

  // ONE ROW RENDERER for both panes' list reading — the brutalist ledger:
  // name in the walls' voice, category / fields / nodes in invisible
  // columns, the private flag at the right trim.
  function _renderSchemaRow(schema) {
    const fields = getFieldNames(schema);
    const nodes = schemaNodeCounts[schema.id] || 0;
    return (
      <div
        key={schema.id}
        className={`schema-lrow ofself-hover ${selectedSchemaId === schema.id ? 'schema-lrow--selected' : ''}`}
        onClick={() => setSelectedSchemaId(selectedSchemaId === schema.id ? null : schema.id)}
      >
        <span className="schema-lrow__name">{schemaLabel(schema.schema_name)}</span>
        <span className="schema-lrow__cell">{schema.category || ''}</span>
        <span className="schema-lrow__cell">{fields.length ? plural(fields.length, 'field') : ''}</span>
        <span className="schema-lrow__cell">{nodes ? plural(nodes, 'node') : ''}</span>
        <span className="schema-lrow__state">{schema.scope === 'private' ? 'private' : ''}</span>
      </div>
    );
  }

  // THE SAME CARD THE LIBRARY DRAWS: one renderer, so a schema reads
  // identically whether you meet it here or there. The pane wears the same
  // floating chrome and list reading as the library (owner call 2026-08-22),
  // sharing its view state and search, so the reading carries across leaves.
  const mySearched = usedSchemas.filter((schema) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (schema.schema_name || '').toLowerCase().includes(q)
      || schemaLabel(schema.schema_name || '').toLowerCase().includes(q)
      || (schema.description || '').toLowerCase().includes(q)
      || getFieldNames(schema).some(f => f.toLowerCase().includes(q));
  });
  const _myPane = loadError ? (
    <div className="hub__pane-scroll schema-loaderr">
      <p className="schema-loaderr__title">Schemas did not load</p>
      <p className="schema-loaderr__why">{loadError}</p>
      <button type="button" className="btn schema-loaderr__retry" onClick={() => { loadSchemas(); loadAllSchemas(); }}>
        Try again
      </button>
    </div>
  ) : (
    <div className="schema-discover">
      <PaneBar>
        <PaneBar.Button active={libView === 'blocks'} label="Blocks view" onClick={() => setLibView('blocks')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="3" width="7.5" height="7.5"/><rect x="13.5" y="3" width="7.5" height="7.5"/><rect x="3" y="13.5" width="7.5" height="7.5"/><rect x="13.5" y="13.5" width="7.5" height="7.5"/></svg>
        </PaneBar.Button>
        <PaneBar.Button active={libView === 'rows'} label="List view" onClick={() => setLibView('rows')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="4" width="18" height="16" rx="1"/><path d="M3 9.3h18M3 14.6h18" strokeLinecap="round"/></svg>
        </PaneBar.Button>
        <PaneBar.Search value={search} onChange={setSearch} placeholder="Search schemas" />
      </PaneBar>
      {libView === 'rows' ? (
        <div className="schema-lrows">
          {mySearched.map(schemaItem => _renderSchemaRow(schemaItem))}
        </div>
      ) : (
        <FillGrid>
          {mySearched.map(schemaItem => _renderSchemaCard(schemaItem))}
        </FillGrid>
      )}
    </div>
  );


  // ==============================
  // DISCOVER VIEW
  // ==============================
  function _renderSchemaCard(schema) {
    const apps = schema.apps || [];
    const isSelected = selectedSchemaId === schema.id;
    const fields = getFieldNames(schema);

    return (
      <Plate
        key={schema.id}
        selected={isSelected}
        onClick={() => setSelectedSchemaId(isSelected ? null : schema.id)}
      >
        <div className="schema-card__header">
          <div className="schema-card__title-row">
            <h3 className="ofself-plate__name">
              {schemaLabel(schema.schema_name)}
            </h3>
            {schema.parent_schema && (
              <span className="schema-card__extends-hint">
                extends {schemaLabel(schema.parent_schema.schema_name)}
              </span>
            )}
          </div>
          <div className="schema-card__badges">
            {schema.scope === 'private' && <Badge variant="danger" className="schema-card__badge">Private</Badge>}
          </div>
        </div>

        {/* description intentionally hidden on cards — it lives in the detail panel */}

        {fields.length > 0 && (
          <div className="schema-card__fields-row">
            <div className="schema-card__fields">
              {fields.slice(0, 6).map(f => (
                <span key={f} className="schema-card__field">{f}</span>
              ))}
              {fields.length > 6 && (
                <span className="schema-card__field schema-card__field--more">+{fields.length - 6}</span>
              )}
            </div>
          </div>
        )}

        {apps.length > 0 && (
          <div className="schema-card__apps-row">
            {apps.map(app => (
              <span key={app.id} className="schema-card__app-pill">
                {app.is_plugin ? '🔌' : '📱'} {app.name}
              </span>
            ))}
          </div>
        )}
      </Plate>
    );
  }

  function _renderDetailPanel() {
    if (!selectedSchema) return null;

    const apps = selectedSchema.apps || [];
    const extendedBy = selectedSchema.extended_by || [];

    const handleToggleParent = async () => {
      if (parentExpanded) {
        setParentExpanded(false);
        return;
      }
      // Fetch parent schema if not already loaded
      if (!parentSchemaData && selectedSchema.extends) {
        try {
          const data = await api.request(`/schemas/${selectedSchema.extends}?include_json_schema=true`);
          setParentSchemaData(data);
        } catch {
          toast.error('Failed to load parent schema');
          return;
        }
      }
      setParentExpanded(true);
    };

    const parentFields = parentSchemaData?.json_schema?.properties
      ? Object.entries(parentSchemaData.json_schema.properties)
      : [];

    // Fields with x-ref-schema: [{field, uris}]
    const refFields = Object.entries(selectedSchema.json_schema?.properties || {})
      .map(([field, prop]) => {
        const refs = prop['x-ref-schema'];
        if (!refs) return null;
        const uris = Array.isArray(refs) ? refs : [refs];
        return { field, uris };
      })
      .filter(Boolean);

    // Schemas that reference this one via x-ref-schema
    const referencedBy = allSchemas.filter(s =>
      s.id !== selectedSchema.id &&
      Object.values(s.json_schema?.properties || {}).some(prop => {
        const refs = prop['x-ref-schema'];
        if (!refs) return false;
        const uris = Array.isArray(refs) ? refs : [refs];
        return uris.includes(selectedSchema.schema_name);
      })
    );

    return (
      <div className="schema-detail">
        <div className="schema-detail__header">
          <div className="schema-detail__header-text">
            <h2 className="schema-detail__name">{schemaLabel(selectedSchema.schema_name)}</h2>
          </div>
          <button className="btn schema-detail__close" aria-label="Close details" onClick={() => setSelectedSchemaId(null)}>
            <ChevronRightIcon size={18} />
          </button>
        </div>

        {selectedSchema.description && (
          <p className="schema-detail__description">{selectedSchema.description}</p>
        )}

        {(
          <>
            <div className="schema-detail__meta">
              <div className="schema-detail__meta-row">
                <span className="schema-detail__meta-label">Version</span>
                {versions.length > 1 ? (
                  <select
                    className="schema-detail__version-select"
                    value={detailSchema?.id || ''}
                    onChange={e => handleVersionSwitch(e.target.value)}
                  >
                    {versions.map(v => (
                      <option key={v.id} value={v.id}>
                        v{v.version} — {dateOf(v.created_at)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="schema-detail__meta-value">v{selectedSchema.version}</span>
                )}
              </div>
              <div className="schema-detail__meta-row">
                <span className="schema-detail__meta-label">Scope</span>
                <Badge variant={selectedSchema.scope === 'public' ? 'success' : 'danger'}>{selectedSchema.scope}</Badge>
              </div>
              {selectedSchema.category && (
                <div className="schema-detail__meta-row">
                  <span className="schema-detail__meta-label">Category</span>
                  <span className="schema-detail__meta-value">{selectedSchema.category}</span>
                </div>
              )}
              {selectedSchema.created_by && (
                <div className="schema-detail__meta-row">
                  <span className="schema-detail__meta-label">Created by</span>
                  <span className="schema-detail__meta-value">{selectedSchema.created_by}</span>
                </div>
              )}
              {selectedSchema.group_id && (
                <div className="schema-detail__meta-row">
                  <span className="schema-detail__meta-label">Owned by</span>
                  <span className="schema-detail__meta-value">
                    {groups.find(g => g.id === selectedSchema.group_id)?.name || 'Group'}
                  </span>
                </div>
              )}
              {selectedSchema.is_system && (
                <div className="schema-detail__meta-row">
                  <span className="schema-detail__meta-label">Status</span>
                  <span className="schema-detail__meta-value" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <ShieldCheck size={14} className="schema-validated-icon" /> Validated
                  </span>
                </div>
              )}
            </div>

            {/* Inheritance — inline expand */}
            {selectedSchema.parent_schema && (
              <div className="schema-detail__section">
                <h4 className="schema-detail__section-title"><GitFork size={14} /> Extends</h4>
                <button
                  className="btn schema-detail__parent-link"
                  onClick={handleToggleParent}
                >
                  <div style={{ flex: 1 }}>
                    <div className="schema-detail__parent-name">{schemaLabel(selectedSchema.parent_schema.schema_name)}</div>
                    <div className="schema-detail__parent-uri">{selectedSchema.parent_schema.schema_name} v{selectedSchema.parent_schema.version}</div>
                  </div>
                  <ChevronDown size={14} style={{ transition: 'transform 0.15s', transform: parentExpanded ? 'rotate(180deg)' : 'rotate(0)' }} />
                </button>
                {parentExpanded && parentSchemaData && (
                  <div className="schema-detail__parent-fields">
                    {parentSchemaData.description && (
                      <p className="schema-detail__parent-desc">{parentSchemaData.description}</p>
                    )}
                    <div className="schema-card__fields">
                      {parentFields.map(([key, prop]) => (
                        <span key={key} className="schema-card__field" title={prop.description || ''}>
                          {key}
                          <span className="schema-detail__field-type">{prop.type || ''}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {extendedBy.length > 0 && (
              <div className="schema-detail__section">
                <h4 className="schema-detail__section-title"><Layers size={14} /> Extended by</h4>
                <div className="schema-detail__children-list">
                  {extendedBy.map(child => (
                    <button
                      key={child.id}
                      className="btn schema-detail__child-link"
                      onClick={() => navigateToSchema(child.schema_name)}
                    >
                      <span>{child.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* References — fields with x-ref-schema */}
            {refFields.length > 0 && (
              <div className="schema-detail__section">
                <h4 className="schema-detail__section-title"><Link size={14} /> References</h4>
                <div className="schema-detail__refs-list">
                  {refFields.map(({ field, uris }) => (
                    <div key={field} className="schema-detail__ref-item">
                      <span className="schema-detail__ref-field">{field}</span>
                      <span className="schema-detail__ref-arrow">→</span>
                      <span className="schema-detail__ref-targets">
                        {uris.map((uri, i) => {
                          const target = allSchemas.find(s => s.schema_name === uri);
                          return target ? (
                            <button
                              key={uri}
                              className="btn schema-detail__ref-link"
                              onClick={() => navigateToSchemaById(target.id)}
                            >{uri}</button>
                          ) : (
                            <span key={uri} className="schema-detail__ref-uri">{uri}</span>
                          );
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Referenced by */}
            {referencedBy.length > 0 && (
              <div className="schema-detail__section">
                <h4 className="schema-detail__section-title"><Link size={14} /> Referenced by</h4>
                <div className="schema-detail__children-list">
                  {referencedBy.map(s => (
                    <button
                      key={s.id}
                      className="btn schema-detail__child-link"
                      onClick={() => navigateToSchemaById(s.id)}
                    >
                      <span>{schemaLabel(s.schema_name)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Apps using this schema */}
            {apps.length > 0 && (
              <div className="schema-detail__section">
                <h4 className="schema-detail__section-title">Apps using this schema</h4>
                <div className="schema-detail__apps-list">
                  {apps.map(app => (
                    <div key={app.id} className="schema-detail__app-item">
                      <span className="schema-detail__app-name">{app.name}</span>
                      {app.is_plugin && <Badge variant="secondary" className="schema-card__badge">Plugin</Badge>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Encrypted fields */}
            {selectedSchema.encrypted_fields?.length > 0 && (
              <div className="schema-detail__section">
                <h4 className="schema-detail__section-title">Encrypted Fields</h4>
                <div className="schema-card__fields">
                  {selectedSchema.encrypted_fields.map(f => (
                    <span key={f} className="schema-card__field schema-card__field--encrypted">{f}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Schema definition */}
            {selectedSchema.json_schema && (
              <div className="schema-detail__section schema-detail__section--definition">
                <div className="schema-detail__def-header">
                  <h4 className="schema-detail__section-title">Schema Definition</h4>
                  <div className="schema-detail__view-toggle">
                    <button
                      className={`btn schema-detail__view-btn ${schemaViewMode === 'form' ? 'schema-detail__view-btn--active' : ''}`}
                      onClick={() => setSchemaViewMode('form')}
                    >Form</button>
                    <button
                      className={`btn schema-detail__view-btn ${schemaViewMode === 'json' ? 'schema-detail__view-btn--active' : ''}`}
                      onClick={() => setSchemaViewMode('json')}
                    >JSON</button>
                  </div>
                </div>
                {schemaViewMode === 'json' ? (
                  <pre className="schema-detail__json">
                    {JSON.stringify(selectedSchema.json_schema, null, 2)}
                  </pre>
                ) : (
                  <SchemaForm
                    schema={selectedSchema.json_schema}
                    values={{}}
                    mode="preview"
                  />
                )}
              </div>
            )}

            {/* Test */}
            <div className="schema-detail__section schema-detail__section--test">
              <h4 className="schema-detail__section-title">Test Data</h4>
              <textarea
                value={testJson}
                onChange={e => { setTestJson(e.target.value); setTestResult(null); }}
                rows={4}
                placeholder='{"field": "value"}'
                className="schema-detail__test-input"
              />
              {testResult && (
                <div className={`schema-detail__test-result ${testResult.valid ? 'schema-detail__test-result--valid' : 'schema-detail__test-result--invalid'}`}>
                  {testResult.valid ? '✓ Valid' : (
                    <>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Invalid</div>
                      {testResult.errors.map((e, i) => <div key={i} style={{ fontFamily: 'monospace', fontSize: 11 }}>{e}</div>)}
                    </>
                  )}
                </div>
              )}
              <button className="btn schema-detail__validate-btn" onClick={handleTest} disabled={testLoading}>
                {testLoading ? 'Validating…' : 'Validate'}
              </button>
            </div>

            {canManageSchema(selectedSchema) && (
              <div className="schema-detail__section schema-detail__actions">
                <Button variant="secondary" size="sm" onClick={openTransferModal}>Transfer ownership</Button>
              </div>
            )}
          </>
        )}

      </div>
    );
  }

  // ==============================
  // BUILDER VIEW
  // ==============================
  if (view === 'builder') {
    return (
      <SchemaBuilder
        mode={builderMode}
        parentSchema={builderParentSchema}
        existingSchema={builderExistingSchema}
        allSchemas={allSchemas}
        onSave={handleBuilderSave}
        onCancel={handleBuilderCancel}
        onDelete={builderMode === 'update' && builderExistingSchema?.scope === 'private' && canManageSchema(builderExistingSchema) ? async () => {
          try {
            await api.request(`/schemas/${builderExistingSchema.id}`, { method: 'DELETE' });
            toast.success('Schema deleted');
            await loadSchemas();
            await loadAllSchemas();
            setView('landing');
          } catch (err) {
            toast.error(err.message || 'Failed to delete schema');
          }
        } : null}
      />
    );
  }

  return (
    <HubSpread
      leaves={_leaves}
      active={libraryOpen ? 'library' : 'mine'}
      onSelect={(id) => { setLibraryOpen(id === 'library'); if (id !== 'library') setSelectedSchemaId(null); }}
    >
      {!libraryOpen ? _myPane : (
    <div className="schema-discover">
      {/* THE FLOATING CHROME (owner call 2026-08-22): the graph page's icon
          array, from the one shared <PaneBar> — blocks/list toggle, the
          collapsible search, and the FILTER flyout that absorbed the old
          search band, owner buttons and category-chip carousel. No bands,
          no seams; the plates run flush to the top. */}
      <PaneBar>
        <PaneBar.Button active={libView === 'blocks'} label="Blocks view" onClick={() => setLibView('blocks')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="3" width="7.5" height="7.5"/><rect x="13.5" y="3" width="7.5" height="7.5"/><rect x="3" y="13.5" width="7.5" height="7.5"/><rect x="13.5" y="13.5" width="7.5" height="7.5"/></svg>
        </PaneBar.Button>
        <PaneBar.Button active={libView === 'rows'} label="List view" onClick={() => setLibView('rows')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="4" width="18" height="16" rx="1"/><path d="M3 9.3h18M3 14.6h18" strokeLinecap="round"/></svg>
        </PaneBar.Button>
        <PaneBar.Button active={filtersOpen || !!ownerFilter || !!categoryFilter} label="Filters" onClick={() => setFiltersOpen(o => !o)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16M7 12h10M10 19h4"/></svg>
        </PaneBar.Button>
        <PaneBar.Search value={search} onChange={setSearch} placeholder="Search schemas" />
        <PaneBar.Flyout open={filtersOpen} onClose={() => setFiltersOpen(false)}>
          <div className="pane-bar__flyout-group">
            <p className="pane-bar__flyout-label">Owner</p>
            <div className="pane-bar__flyout-chips">
              <button
                className={`btn schema-discover__filter-btn ${ownerFilter === '' ? 'schema-discover__filter-btn--active' : ''}`}
                onClick={() => setOwnerFilter('')}
              >All</button>
              <button
                className={`btn schema-discover__filter-btn ${ownerFilter === 'system' ? 'schema-discover__filter-btn--active' : ''}`}
                onClick={() => setOwnerFilter(ownerFilter === 'system' ? '' : 'system')}
              >System</button>
              <button
                className={`btn schema-discover__filter-btn ${ownerFilter === 'mine' ? 'schema-discover__filter-btn--active' : ''}`}
                onClick={() => setOwnerFilter(ownerFilter === 'mine' ? '' : 'mine')}
              >Mine</button>
            </div>
          </div>
          {categories.length > 0 && (
            <div className="pane-bar__flyout-group">
              <p className="pane-bar__flyout-label">Category</p>
              <div className="pane-bar__flyout-chips">
                {categories.map(cat => (
                  <button
                    key={cat}
                    className={`btn schema-discover__category-chip ${categoryFilter === cat ? 'schema-discover__category-chip--active' : ''}`}
                    onClick={() => setCategoryFilter(categoryFilter === cat ? '' : cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}
        </PaneBar.Flyout>
      </PaneBar>

      {/* Main content area */}
      <div className="schema-discover__body">
        <div className={`schema-discover__list ${selectedSchemaId ? 'schema-discover__list--with-detail' : ''} ${listMode === 'graph' ? 'schema-discover__list--graph' : ''}`}>
          {listMode === 'graph' ? (
            <SchemaNetworkGraph
              schemas={schemas}
              onNavigate={id => setSelectedSchemaId(id)}
              selectedId={selectedSchemaId}
              highlightSearch={search}
            />
          ) : loading ? (
            <div className="schema-discover__empty">Loading schemas…</div>
          ) : filteredSchemas.length === 0 ? (
            <div className="schema-discover__empty">
              <h3>No schemas found</h3>
              <p>{search ? 'Try a different search term.' : 'No schemas match the current filters.'}</p>
            </div>
          ) : libView === 'rows' ? (
            <div className="schema-lrows">
              {filteredSchemas.map(schema => _renderSchemaRow(schema))}
            </div>
          ) : (
            <FillGrid>
              {filteredSchemas.map(schema => _renderSchemaCard(schema))}
            </FillGrid>
          )}
        </div>

        {selectedSchemaId && (
          <div className="schema-discover__detail-panel">
            {_renderDetailPanel()}
          </div>
        )}
      </div>

      {showTransferModal && selectedSchema && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Transfer "{schemaLabel(selectedSchema.schema_name)}"</h3>
              <button className="btn modal-close" onClick={() => setShowTransferModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0, fontSize: 13, color: 'var(--text-secondary, #888)' }}>
                Transfers <strong>all versions</strong> of <code>{selectedSchema.schema_name}</code> to the new owner.
              </p>
              <div className="transfer-type-tabs">
                <button
                  className={`btn transfer-tab ${transferType === 'user' ? 'transfer-tab--active' : ''}`}
                  onClick={() => { setTransferType('user'); setTransferTarget(''); setTransferError(''); }}
                >To a user</button>
                <button
                  className={`btn transfer-tab ${transferType === 'group' ? 'transfer-tab--active' : ''}`}
                  onClick={() => { setTransferType('group'); setTransferTarget(''); setTransferError(''); }}
                >To a group</button>
                {selectedSchema.group_id && (
                  <button
                    className={`btn transfer-tab ${transferType === 'personal' ? 'transfer-tab--active' : ''}`}
                    onClick={() => { setTransferType('personal'); setTransferTarget(''); setTransferError(''); }}
                  >To my account</button>
                )}
              </div>

              {transferType === 'user' && (
                <div className="transfer-input-wrap">
                  <label className="transfer-label">Search for a user</label>
                  <div className="transfer-user-search">
                    <Input
                      value={userSearchQuery}
                      onChange={e => handleUserSearchChange(e.target.value)}
                      placeholder="Search by username or email…"
                      autoFocus
                      autoComplete="off"
                    />
                    {(userSearchResults.length > 0 || userSearchLoading) && !selectedUser && (
                      <div className="transfer-user-dropdown">
                        {userSearchLoading && <div className="transfer-user-option transfer-user-option--loading">Searching…</div>}
                        {userSearchResults.map(u => (
                          <button
                            key={u.user_id}
                            className="btn transfer-user-option"
                            type="button"
                            onClick={() => selectTransferUser(u)}
                          >
                            <span className="transfer-user-name">{u.username}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedUser && (
                      <div className="transfer-user-selected">
                        <span>{selectedUser.username}</span>
                        <button type="button" className="btn transfer-user-clear" onClick={() => { setSelectedUser(null); setTransferTarget(''); setUserSearchQuery(''); }}>×</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {transferType === 'group' && (
                <div className="transfer-input-wrap">
                  <label className="transfer-label">Select group</label>
                  <Select
                    value={transferTarget}
                    onChange={e => setTransferTarget(e.target.value)}
                  >
                    <option value="">— choose a group —</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </Select>
                </div>
              )}

              {transferType === 'personal' && (
                <p className="transfer-personal-note">
                  This will move the schema from the group to your personal account. You will become the sole owner.
                </p>
              )}

              {transferError && <p className="transfer-error">{transferError}</p>}
            </div>
            <div className="modal-footer">
              <Button variant="secondary" size="sm" onClick={() => setShowTransferModal(false)}>Cancel</Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleTransfer}
                disabled={transferLoading || (transferType === 'user' && !transferTarget) || (transferType === 'group' && !transferTarget)}
              >
                {transferLoading ? 'Transferring…' : 'Transfer ownership'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
      )}
    </HubSpread>
  );
}

export default DevSchemasTab;
